import { useState, useCallback, useEffect, memo } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import SurveyHistoryInfo from '../../shared/surveyHistoryInfo';
import SurveyHistory from '../../shared/surveyHistory';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import http from '../../../utility/http';
import { useTranslations } from '../../../utility/useTranslations';
import {
  prepareSurveyData,
  prepareAttributeReviewData,
  prepareQuestionnaireReviewData,
} from '../../../utility/fillSurveyUtils';
import {
  api_survey_history,
  api_attribute_review_history,
  api_questionnaire_review_history,
} from '../../../constants/apiRoutes';
import { APP_PAGES } from '../../../constants/pages';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  content: {
    boxSizing: 'border-box',
    margin: '0 auto',
    width: 930,
    maxWidth: 930,
  },
  title: {
    maxWidth: 'calc(100% - 260px)',
    width: '100%',
  },
  anonymousNotification: {
    backgroundColor: primary.bluish8,
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    padding: spacing(4),
    marginTop: spacing(4),
    marginLeft: 'auto',
    marginRight: 'auto',
    width: 'fit-content',
  },
}));

const SurveyHistoryPage = ({ location, auth, navigate, ...rest }) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SURVEY_HISTORY);
  const { token } = useParams();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [surveyData, setSurveyData] = useState(null);
  const [reviews, setReviews] = useState([]);

  const getAttributeReview = async (surveyToken, reviewId) => {
    const { data } = await http.get(
      api_attribute_review_history(surveyToken, reviewId)
    );
    return prepareAttributeReviewData(data);
  };

  const getQuestionnaireReview = async (surveyToken, reviewId) => {
    const { data } = await http.get(
      api_questionnaire_review_history(surveyToken, reviewId)
    );
    return prepareQuestionnaireReviewData(data);
  };

  const onLoadNextReview = useCallback(
    async currentReviewId => {
      if (!surveyData || surveyData.attributes.length === 1) return;
      const currentReviewIndex = surveyData.attributes.findIndex(
        review => review.id === currentReviewId
      );

      const nextReviewId = surveyData.attributes[currentReviewIndex + 1]?.id;
      if (nextReviewId) {
        const nextReview = await getAttributeReview(token, nextReviewId);

        setReviews(prev => [...prev, nextReview]);
      }
    },
    [surveyData, token]
  );

  const getInitialData = useCallback(async () => {
    const { data } = await http.get(api_survey_history(token));
    const { attributes, questionnaire } = data;

    const firstReview = questionnaire
      ? await getQuestionnaireReview(token, questionnaire.id)
      : await getAttributeReview(token, attributes[0].id);

    setSurveyData(prepareSurveyData(data));
    setReviews([firstReview]);

    setIsInitialLoad(false);
  }, [token]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const renderSurveyTitle = () => {
    return (
      <div className={classes.title}>
        <TextBoxWithTooltip variant="h1" text={surveyData?.surveyTitle} />
      </div>
    );
  };

  return (
    !isInitialLoad && (
      <PageContainer
        {...rest}
        translations={translations}
        location={location}
        navigate={navigate}
        auth={auth}
        shouldPassProps={false}
        headerProps={{
          renderTitle: renderSurveyTitle,
        }}
        isFullWidthContent
      >
        <div>
          <SurveyHistoryInfo
            translations={translations.info}
            survey={surveyData}
          />
          {surveyData?.is_anonymous && (
            <Typography
              className={classes.anonymousNotification}
              variant="subtitle2"
            >
              {translations.anonymousSurveyInfo}
            </Typography>
          )}
          <div className={classes.content}>
            <SurveyHistory
              translations={translations.survey}
              reviews={reviews}
              survey={surveyData}
              onLoadNextReview={onLoadNextReview}
            />
          </div>
        </div>
      </PageContainer>
    )
  );
};

export default memo(SurveyHistoryPage);
